import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs, { Dayjs } from 'dayjs';
import { type ReactNode, useMemo } from 'react';

import { type Segment } from '~pages/LiveMap/Timeline/components/hooks/useGetCycleSegments';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

// ---------------------------------------------------------------------------
// shared utilities
// ---------------------------------------------------------------------------

export function getNoSignalBlocks(
  segments: Segment[],
  minTime: Dayjs,
  maxTime: Dayjs,
): [Dayjs, Dayjs][] {
  const blocks: [Dayjs, Dayjs][] = [];
  if (segments.length === 0) {
    blocks.push([minTime, maxTime]);
    return blocks;
  }

  if (segments[0].start.isAfter(minTime)) {
    blocks.push([minTime, segments[0].start]);
  }

  for (let i = 0; i < segments.length - 1; i++) {
    const current = segments[i];
    const next = segments[i + 1];
    if (current?.end && next?.start) {
      blocks.push([current.end, next.start]);
    }
  }

  if (segments[segments.length - 1].end.isBefore(maxTime)) {
    blocks.push([segments[segments.length - 1].end, maxTime]);
  }

  return blocks;
}

const TRUCK_ARROW_PATH = 'M14 0 28 30 14 24 0 30 14 0';

export const MOVING_SITE_CIRCLE_OPTIONS: google.maps.CircleOptions = {
  fillColor: 'black',
  fillOpacity: 0.15,
  strokeColor: 'black',
  strokeOpacity: 0.6,
  strokeWeight: 1,
  clickable: false,
};

export function makeTruckMarkerIcon(color: string, bearing: number): google.maps.Symbol {
  return {
    path: TRUCK_ARROW_PATH,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: 'white',
    strokeWeight: 2,
    scale: 0.8,
    rotation: bearing,
    anchor: new window.google.maps.Point(14, 15),
  };
}

// ---------------------------------------------------------------------------
// tick computation
// ---------------------------------------------------------------------------

interface TickIntervals {
  major: number;
  minor: number;
}

// ordered largest → smallest so we can pick the first that gives enough ticks
const TICK_CANDIDATES: TickIntervals[] = [
  { major: 12 * HOUR, minor: 3 * HOUR },
  { major: 6 * HOUR, minor: HOUR },
  { major: 3 * HOUR, minor: HOUR },
  { major: HOUR, minor: 15 * MINUTE },
  { major: 30 * MINUTE, minor: 5 * MINUTE },
  { major: 15 * MINUTE, minor: 5 * MINUTE },
  { major: 5 * MINUTE, minor: MINUTE },
  { major: MINUTE, minor: MINUTE },
];

const MIN_MAJOR_TICKS = 2;
const MAX_MAJOR_TICKS = 8;

// minimum percentage from the edge before we show a tick label
const EDGE_MARGIN = 0.08;

export function generateTicks(
  startMs: number,
  endMs: number,
  intervalMs: number,
): number[] {
  const first = Math.ceil(startMs / intervalMs) * intervalMs;
  const ticks: number[] = [];

  for (let ts = first; ts < endMs; ts += intervalMs) {
    if (ts > startMs) ticks.push(ts);
  }

  return ticks;
}

export function chooseTicks(startMs: number, endMs: number): TickIntervals {
  for (const candidate of TICK_CANDIDATES) {
    const count = generateTicks(startMs, endMs, candidate.major).length;
    if (count >= MIN_MAJOR_TICKS && count <= MAX_MAJOR_TICKS) {
      return candidate;
    }
    if (count > MAX_MAJOR_TICKS) {
      // all smaller intervals would produce even more ticks — use this one
      return candidate;
    }
  }

  // range is too small for any candidate — use the smallest interval
  return TICK_CANDIDATES[TICK_CANDIDATES.length - 1];
}

function formatTickLabel(ms: number): string {
  const d = dayjs.tz(ms);
  return d.minute() === 0 ? d.format('h A') : d.format('h:mm A');
}

// ---------------------------------------------------------------------------
// shared tick lines (rendered inside the graph box)
// ---------------------------------------------------------------------------

interface TickLinesProps {
  startMs: number;
  endMs: number;
  normalize: (time: string, inverted?: boolean) => string;
}

export function TimelineTickLines({ startMs, endMs, normalize }: TickLinesProps) {
  const theme = useTheme();
  const { major: majorInterval, minor: minorInterval } = useMemo(
    () => chooseTicks(startMs, endMs),
    [startMs, endMs],
  );

  const majorTicks = useMemo(
    () => generateTicks(startMs, endMs, majorInterval),
    [startMs, endMs, majorInterval],
  );
  const minorTicks = useMemo(
    () => generateTicks(startMs, endMs, minorInterval),
    [startMs, endMs, minorInterval],
  );
  const majorSet = useMemo(() => new Set(majorTicks), [majorTicks]);

  return (
    <>
      {minorTicks.map((tick) => {
        if (majorSet.has(tick)) return null;

        return (
          <Box
            key={`minor-${tick}`}
            sx={{
              position: 'absolute',
              left: normalize(dayjs(tick).toISOString()),
              width: '1px',
              height: '100%',
              backgroundColor: theme.brandV2.colors.treadGray6,
              opacity: 0.25,
            }}
          />
        );
      })}

      {majorTicks.map((tick) => (
        <Box
          key={`major-${tick}`}
          sx={{
            position: 'absolute',
            left: normalize(dayjs(tick).toISOString()),
            width: '1px',
            height: '100%',
            backgroundColor: theme.brandV2.colors.treadGray6,
          }}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// shared tick labels (rendered below the graph box)
// ---------------------------------------------------------------------------

interface TickLabelsProps {
  startMs: number;
  endMs: number;
  normalize: (time: string, inverted?: boolean) => string;
}

export function TimelineTickLabels({ startMs, endMs, normalize }: TickLabelsProps) {
  const durationMs = endMs - startMs;
  const { major: majorInterval } = useMemo(
    () => chooseTicks(startMs, endMs),
    [startMs, endMs],
  );
  const majorTicks = useMemo(
    () => generateTicks(startMs, endMs, majorInterval),
    [startMs, endMs, majorInterval],
  );

  const pct = (ms: number) => (durationMs > 0 ? (ms - startMs) / durationMs : 0);

  return (
    <Box sx={{ position: 'relative', height: 20, mt: '2px' }}>
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          left: 0,
          color: 'text.secondary',
          fontSize: '0.625rem',
        }}
      >
        {dayjs.tz(startMs).format('h:mm A')}
      </Typography>

      {majorTicks.map((tick) => {
        const p = pct(tick);
        if (p < EDGE_MARGIN || p > 1 - EDGE_MARGIN) return null;

        return (
          <Typography
            key={`label-${tick}`}
            variant="caption"
            sx={{
              position: 'absolute',
              left: normalize(dayjs(tick).toISOString()),
              transform: 'translateX(-50%)',
              color: 'text.secondary',
              fontSize: '0.625rem',
              whiteSpace: 'nowrap',
            }}
          >
            {formatTickLabel(tick)}
          </Typography>
        );
      })}

      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          right: 0,
          color: 'text.secondary',
          fontSize: '0.625rem',
        }}
      >
        {dayjs.tz(endMs).format('h:mm A')}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// shared time display (rendered above the graph)
// ---------------------------------------------------------------------------

interface TimeDisplayProps {
  indicatorTime: number;
  label: string;
  children?: ReactNode;
}

export function TimelineTimeDisplay({
  indicatorTime,
  label,
  children,
}: TimeDisplayProps) {
  const formattedTime = indicatorTime ? dayjs.tz(indicatorTime).format('h:mm A') : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minHeight: 24 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {label && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ visibility: formattedTime ? 'visible' : 'hidden' }}
        >
          {formattedTime ?? '\u00A0'}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}
